import * as React from 'react';
import { DefaultButton } from 'office-ui-fabric-react';
import { ISectionUIControlProps } from '../../../interfaces/ISectionUIControlProps';
import { Attachment, AttachmentType } from '../../../../../entities/Subentities';
import { SectionControlPlaceholder } from '../../SectionPlaceholder';
import { mergeDeep } from '../../../../../store/utils';
import { nameof } from '../../../../../store/services/metadataService';
import { orderByIds } from '../../../../utils/common';
import { ImagesSlider } from './ImagesSlider';
import { ConfigurePanel } from './ConfigurePanel';
import { IActions, ISettings, IControlSettings, IDataContext } from './types';

type Props = ISectionUIControlProps<
    IActions,
    ISettings,
    { attachments: Attachment[] },
    {},
    IControlSettings,
    IDataContext
>;

const defaultConfiguration: ISettings = {
    contentHeight: 600,
    imagesIds: []
};

export const controlSettingsUpdate = (showConfigurePanel: boolean) => ({
    parentSettingsPathKeys: [],
    valueBySettingNameMap: {
        [nameof<IControlSettings>("showConfigurePanel")]: showConfigurePanel
    }
});

const ImagesControl = (props: Props) => {
    const images = props.entity.attachments.filter(_ => _.attributes.Type === AttachmentType.Image);
    const selected = orderByIds(images, props.settings.imagesIds ?? [], _ => _.id);
    const settings = mergeDeep(defaultConfiguration, props.settings);

    return (
        <>
            {selected.length === 0 ? (
                <SectionControlPlaceholder
                    key="no-data"
                    iconName="Photo2"
                    title="Images section has not been configured yet"
                    description="Select one or several images from Attachments to be displayed in the section."
                >
                    {showConfigureImages(props.entity, props.isViewSelected, props.datacontext) && (
                        <DefaultButton
                            text="Select Images"
                            onClick={() => props.onSaveSettings?.(controlSettingsUpdate(true), false)}
                        />
                    )}
                </SectionControlPlaceholder>
            ) : (
                <ImagesSlider
                    images={selected}
                    actions={props.actions}
                    contentHeight={settings.contentHeight}
                />
            )}
            {props.controlSettings.showConfigurePanel && (
                <ConfigurePanel
                    readOnly={!canManage(props.entity, props.isViewSelected, props.datacontext)}
                    images={images}
                    onDismiss={() => props.onSaveSettings?.(controlSettingsUpdate(false), false)}
                    value={settings}
                    onComplete={_ => props.actions.updateUiControl(props.sectionId, props.id, _)}
                />
            )}
        </>
    );
}

type ShowConfigureImagesFunc = (entity: { canConfigure?: boolean; isArchived?: boolean }, isViewSelected?: boolean, context?: IDataContext) => boolean | undefined;

const canManage = (entity: { canConfigure?: boolean; isArchived?: boolean }, isViewSelected?: boolean, context?: IDataContext) => isViewSelected
    ? context?.canManageConfiguration
    : !!entity.canConfigure

export const showConfigureImages: ShowConfigureImagesFunc = (entity, isViewSelected, context) => (!!entity.canConfigure && !isViewSelected)
    || context?.canManageConfiguration
    || entity.isArchived;

export default ImagesControl;